import { convertLength, documentDistanceToUnit, lengthBetweenDots } from "./coordinates.js";

export function calculateObjectMeasurements(object, dotsById, options = {}) {
  if (!object || object.visible === false) {
    return [];
  }

  const dimensions = Array.isArray(object.dimensions) && object.dimensions.length > 0
    ? object.dimensions.map((dimension, index) => normalizeExplicitDimension(object, dimension, index))
    : inferDimensions(object);

  return dimensions.flatMap((dimension) => {
    const [start, end] = resolveDimensionDots(dimension, dotsById);

    if (!start || !end) {
      return [];
    }

    const unit = dimension.unit ?? options.displayUnit ?? options.coordinates?.displayUnit ?? options.coordinates?.unit ?? "m";
    const size = documentDistanceToUnit(lengthBetweenDots(start, end), options.coordinates, unit);
    const displayUnit = options.displayUnit ?? unit;
    const displaySize = convertLength(size, unit, displayUnit);

    return [{
      ...dimension,
      objectId: object.id,
      unit,
      size,
      displayUnit,
      displaySize,
      start,
      end,
      label: formatMeasurementLabel(displaySize, displayUnit, options.precision)
    }];
  });
}

function normalizeExplicitDimension(object, dimension, index) {
  return {
    id: dimension.id ?? `${object.id ?? object.type}-dimension-${index + 1}`,
    type: dimension.type ?? "length",
    points: Array.isArray(dimension.points) ? [...dimension.points] : getDefaultDimensionPoints(object, index),
    unit: dimension.unit,
    size: dimension.size
  };
}

function inferDimensions(object) {
  if (object.type === "line") {
    const points = getDefaultDimensionPoints(object, 0);
    return points.length === 2 ? [{ id: `${object.id ?? "line"}-length`, type: "length", points }] : [];
  }

  if (object.type === "rectangle") {
    const points = Array.isArray(object.points) ? object.points.slice(0, 4) : [];

    if (points.length < 4) {
      return [];
    }

    return [
      { id: `${object.id ?? "rectangle"}-top`, type: "length", points: [points[0], points[1]] },
      { id: `${object.id ?? "rectangle"}-right`, type: "length", points: [points[1], points[2]] },
      { id: `${object.id ?? "rectangle"}-bottom`, type: "length", points: [points[2], points[3]] },
      { id: `${object.id ?? "rectangle"}-left`, type: "length", points: [points[3], points[0]] }
    ];
  }

  return [];
}

function getDefaultDimensionPoints(object) {
  return Array.isArray(object.points) ? object.points.slice(0, 2) : [];
}

function resolveDimensionDots(dimension, dotsById) {
  const points = Array.isArray(dimension.points) ? dimension.points : [];

  return points.slice(0, 2).map((pointId) => dotsById.get(pointId) ?? null);
}

function formatMeasurementLabel(value, unit, precision = 2) {
  const normalizedPrecision = Number.isInteger(precision) && precision >= 0 ? precision : 2;

  return `${Number(value).toFixed(normalizedPrecision)} ${unit}`;
}
